import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// API Services
import ProductsService from "../../../api/product";

// Utilities
// MATERIAL UI COMPONENTS
import {
  Alert,
  alpha,
  Box,
  Grid,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";

// ICONS
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AssessmentIcon from "@mui/icons-material/Assessment";
// APP COMPONENTS
// FILTERS
import DateFilter from "../../search/dateFilter";
import SearchFilter from "../../search/searchFilter";
import ViewSelector from "../../selectors/ViewSelector";
import CardsReportView from "./CardsView";
import RestaurantStatsView from "./RestaurantStatsView/RestaurantStatsView";
import DeliveryOrdersReportView from "./DeliveryStatsView/DeliveryOrdersReportView";
import DeliveryAnalyticsDashboard from "./DeliveryStatsView/DeliveryAnalyticsDashboard";
import { setDateRange } from "../../../utils/filters";
import ProductCatalogReportView from "./ProductCatalogReportView/ProductCatalogReportView";
import OrdersJourney from "./OrdersJourney";
import ProductsJourney from "./ProductsJourney";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

export default function OperationalReports() {
  const theme = useTheme();

  const [viewMode, setViewMode] = useState("general");

  const [dateStart, setDateStart] = useState(
    new Date(new Date().setHours(0, 0, 0, 0)).toISOString().split("T")[0]
  );

  const [dateEnd, setDateEnd] = useState(
    new Date(new Date().setHours(0, 0, 0, 0) + 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0]
  );

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [products, setProducts] = useState([]);

  const search = async (filtersData) => {
    try {
      setLoading(true);
      setError(null);

      const filtersObj = {
        ...setDateRange(filtersData, DateFilter.name, { useISOFormat: false }),
      };

      console.log("Final date range:", filtersObj);
      setDateStart(filtersObj.dateStart);
      setDateEnd(filtersObj.dateEnd);
    } catch (e) {
      console.error("Error searching with filters:", e);
      setError("Erro ao filtrar dados. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // todo: Analyze the unused functions so we can add their data on a report screen
  const loadProductsData = async (dateStart, dateEnd) => {
    try {
      const params = {
        start_date: dateStart,
        end_date: dateEnd,
      };
      const { data } = await ProductsService.getProductsReportData(params);

      const productsWithTitle = data.map((product) => {
        return {
          ...product,
          quantity: product.total_quantity,
        };
      });

      productsWithTitle.sort((a, b) => b.quantity - a.quantity);

      setProducts(productsWithTitle);
    } catch (e) {
      console.error("Error loading products data:", e);
      throw e;
    }
  };

  const renderSkeletons = () => {
    return (
      <Grid container spacing={3}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={3} key={`skeleton-card-${index}`}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={200}
              sx={{ borderRadius: 2, mb: 2 }}
            />
          </Grid>
        ))}
        {Array.from({ length: 2 }).map((_, index) => (
          <Grid item xs={12} md={6} key={`skeleton-chart-${index}`}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={300}
              sx={{ borderRadius: 2, mb: 2 }}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: alpha(theme.palette.background.default, 0.98),
        borderRadius: 2,
        boxShadow: "0 0 20px rgba(0,0,0,0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AssessmentIcon
            sx={{
              fontSize: 32,
              color: theme.palette.primary.main,
            }}
          />
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: theme.palette.text.primary }}
          >
            Relatório Operacional
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{ width: "100%", display: "flex", justifyContent: "end", mb: 2 }}
      >
        <ViewSelector viewMode={viewMode} setViewMode={setViewMode} />
      </Box>

      {/* Search Filter */}
      {viewMode !== "thirty_days" && viewMode !== "ai_analysis" && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box
            elevation={0}
            sx={{
              mb: 4,
            }}
          >
            <SearchFilter
              title="Filtrar Período"
              filters={[]}
              filterDate
              submit={search}
              initialExpanded
              showHeader={false}
            />
          </Box>
        </motion.div>
      )}

      {/* Error message display */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ mb: 3, borderRadius: 2 }}
              variant="filled"
              icon={<ErrorOutlineIcon />}
            >
              {error}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      {loading ? (
        renderSkeletons()
      ) : (
        <Box sx={{ height: "100%" }}>
          {viewMode === "thirty_days" && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <RestaurantStatsView />
            </motion.div>
          )}

          {viewMode === "general" && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <CardsReportView startDate={dateStart} endDate={dateEnd} />
            </motion.div>
          )}

          {viewMode === "orders_journey" && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <OrdersJourney startDate={dateStart} endDate={dateEnd} />
            </motion.div>
          )}

          {viewMode === "products_journey" && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <ProductsJourney startDate={dateStart} endDate={dateEnd} />
            </motion.div>
          )}

          {viewMode === "delivery_data" && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <DeliveryOrdersReportView
                startDate={dateStart}
                endDate={dateEnd}
              />
            </motion.div>
          )}

          {viewMode === "catalog" && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <ProductCatalogReportView
                startDate={dateStart}
                endDate={dateEnd}
              />
            </motion.div>
          )}

          {viewMode === "ai_analysis" && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <DeliveryAnalyticsDashboard />
            </motion.div>
          )}
        </Box>
      )}

      {/* Footer with timestamp */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Relatório gerado em: {new Date().toLocaleString("pt-BR")}
        </Typography>
      </Box>
    </Box>
  );
}
